import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { characterAction, fetchCharacterView } from './api';

/**
 * Stands in for the character in a scene until there is one.
 *
 * Every control here is built from the schemas the run reports, so the panel offers exactly what
 * the scene declares — a tool the character gains appears without anyone editing this file, and
 * one it never declared cannot be pressed. The names and the words are the server's, untranslated:
 * this is a developer sending what the character would have sent.
 */
export default function CharacterPanel({ runId, onFrame }) {
  const { t } = useTranslation();
  const [view, setView] = useState(null);
  const [filled, setFilled] = useState({});
  const [answer, setAnswer] = useState(null);
  const [unreadable, setUnreadable] = useState(false);

  useEffect(() => {
    fetchCharacterView(runId)
      .then((reported) => {
        setView(reported);
        setUnreadable(false);
      })
      .catch(() => setUnreadable(true));
  }, [runId]);

  // Only what the developer typed. What a field shows until they do is the schema's to say, so it
  // is derived where it is read rather than copied into state the moment a view arrives.
  const fill = (tool, field, value) =>
    setFilled((current) => ({
      ...current,
      [tool]: { ...current[tool], [field]: value },
    }));

  // The frame is handed on unread: it is what the character's own call would have pushed down the
  // socket, and this panel has no voice to hold it behind.
  const act = (tool) => {
    characterAction(runId, tool.name, argumentsOf(tool, filled[tool.name] ?? {}))
      .then((outcome) => {
        setAnswer(outcome.toModel);
        if (outcome.toStudent) {
          onFrame(JSON.parse(outcome.toStudent));
        }
      })
      .catch(() => setAnswer(null));
  };

  // Whether a panel is on offer is the deployment's call, and the run already carries it; a view
  // that cannot be read is a fault worth saying out loud rather than a panel that deletes itself.
  if (!view) {
    return (
      <aside className="bakery-panel" aria-label={t('characterPanel.heading')}>
        <h2>{t('characterPanel.heading')}</h2>
        <p className="bakery-panel-answer">
          {unreadable ? t('characterPanel.unreadable') : t('characterPanel.loading')}
        </p>
      </aside>
    );
  }

  return (
    <aside className="bakery-panel" aria-label={t('characterPanel.heading')}>
      <h2>{t('characterPanel.heading')}</h2>
      <p className="bakery-panel-explain">{t('characterPanel.explain')}</p>

      <h3>{t('characterPanel.knows')}</h3>
      <pre className="bakery-panel-brief">{view.brief}</pre>

      {view.tools.map((tool) => (
        <fieldset key={tool.name} className="bakery-panel-tool" aria-label={tool.name}>
          <legend>{tool.name}</legend>
          <p className="bakery-panel-explain">{tool.description}</p>
          {fieldsOf(tool).map(([name, schema]) => (
            <Field
              key={name}
              id={`character-panel-${tool.name}-${name}`}
              name={name}
              schema={schema}
              value={shownIn(schema, filled[tool.name]?.[name])}
              onChange={(value) => fill(tool.name, name, value)}
            />
          ))}
          <button type="button" onClick={() => act(tool)}>
            {tool.name}
          </button>
        </fieldset>
      ))}

      {answer && (
        <p className="bakery-panel-answer">
          {t('characterPanel.lastAnswer')}: {answer}
        </p>
      )}
    </aside>
  );
}

/** One argument, drawn as its own bit of schema says it should be. */
function Field({ id, name, schema, value, onChange }) {
  const change = (event) => onChange(event.target.value);
  return (
    <>
      <label htmlFor={id}>{name}</label>
      {schema.enum ? (
        <select id={id} value={value} onChange={change}>
          {schema.enum.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={isANumber(schema) ? 'number' : 'text'}
          min={schema.minimum}
          value={value}
          onChange={change}
        />
      )}
    </>
  );
}

const isANumber = (schema) => schema.type === 'integer' || schema.type === 'number';

/** What a tool takes, in the order its schema names it. A tool that takes nothing takes nothing. */
const fieldsOf = (tool) => Object.entries(tool.parameters?.properties ?? {});

/** What was typed, or what the schema opens on: the first thing offered, or the smallest number. */
const shownIn = (schema, typed) => {
  if (typed !== undefined) {
    return typed;
  }
  if (schema.enum) {
    return schema.enum[0] ?? '';
  }
  return isANumber(schema) ? String(schema.minimum ?? 1) : '';
};

/** What the form was filled with, typed as the schema says rather than as the browser reads it. */
const argumentsOf = (tool, filled) =>
  Object.fromEntries(
    fieldsOf(tool).map(([name, schema]) => {
      const value = shownIn(schema, filled[name]);
      return [name, isANumber(schema) ? Number(value) : value];
    }),
  );
